import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import Navbar from '../components/Navbar';
import PortfolioCard from '../components/PortfolioCard';
import EditPortfolioModal from '../components/EditPortfolioModal';
import CreatePortfolioModal from '../components/CreatePortfolioModal';
import { Card } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { getPortfolios, createPortfolio, updatePortfolio, deletePortfolio } from '../services/api';
import { analytics } from '../lib/analytics';
import type { Portfolio, CreatePortfolioRequest } from '../types';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);

  // Fetch portfolios on mount
  useEffect(() => {
    const fetchPortfolios = async () => {
      setIsLoading(true);
      setError(null);
      
      const response = await getPortfolios();
      
      if (response.success === false) {
        setError(response.error.message || 'Failed to load portfolios');
      } else {
        setPortfolios(response.data);
      }
      
      setIsLoading(false);
    };

    fetchPortfolios();
  }, []);

  const handleViewPortfolio = (portfolio: Portfolio) => {
    // Track portfolio view (position count tracked on the portfolio page)
    analytics.trackPortfolioViewed(portfolio.id, 0);
    navigate(`/portfolio/${portfolio.id}/current`);
  };

  const handleEditPortfolio = (portfolio: Portfolio) => {
    setEditingPortfolio(portfolio);
  };

  const handleDeletePortfolio = async (portfolio: Portfolio) => {
    // Confirm deletion
    const confirmed = window.confirm(
      `Are you sure you want to delete "${portfolio.name}"? This action cannot be undone.`
    );
    
    if (!confirmed) {
      return;
    }

    try {
      const response = await deletePortfolio(portfolio.id);
      
      if (response.success === true) {
        // Track portfolio deletion
        analytics.trackPortfolioDeleted(portfolio.id, portfolio.name);
        // Remove the portfolio from the list
        setPortfolios(portfolios.filter(p => p.id !== portfolio.id));
      } else if (response.success === false) {
        console.error('Failed to delete portfolio:', response.error);
        alert(`Failed to delete portfolio: ${response.error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting portfolio:', error);
      alert('An error occurred while deleting the portfolio');
    }
  };

  const handleCreateNew = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreatePortfolio = async (data: CreatePortfolioRequest) => {
    try {
      const response = await createPortfolio(data);
      
      if (response.success === true && response.data) {
        // Track portfolio creation
        analytics.trackPortfolioCreated(response.data.id, response.data.name, response.data.totalSize);
        // Add the new portfolio to the list
        setPortfolios([...portfolios, response.data]);
        setIsCreateModalOpen(false);
      } else if (response.success === false) {
        console.error('Failed to create portfolio:', response.error);
        alert(`Failed to create portfolio: ${response.error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating portfolio:', error);
      alert('An error occurred while creating the portfolio');
    }
  };

  const handleUpdatePortfolio = async (portfolioId: string, data: any) => {
    try {
      const response = await updatePortfolio(portfolioId, data);
      
      if (response.success === true && response.data) {
        // Track portfolio update
        analytics.trackPortfolioUpdated(portfolioId, data);
        // Update the portfolio in the list
        setPortfolios(portfolios.map(p => p.id === portfolioId ? response.data : p));
        setEditingPortfolio(null);
      } else if (response.success === false) {
        console.error('Failed to update portfolio:', response.error);
        alert(`Failed to update portfolio: ${response.error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating portfolio:', error);
      alert('An error occurred while updating the portfolio');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        portfolios={portfolios}
        selectedPortfolio={null}
        onPortfolioChange={() => {}}
        user={user}
      />

      <div className="max-w-[1600px] mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome back, {user?.name || 'User'}</h1>
          <p className="text-lg text-gray-600">Manage and track your investment portfolios with ease</p>
        </div>

        {/* Portfolios Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Your Portfolios</h2>
          
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your portfolios...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 font-medium mb-2">Failed to load portfolios</p>
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          )}

          {/* Portfolios Grid */}
          {!isLoading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {portfolios.map((portfolio) => (
                <PortfolioCard
                  key={portfolio.id}
                  portfolio={portfolio}
                  onView={handleViewPortfolio}
                  onEdit={handleEditPortfolio}
                  onDelete={handleDeletePortfolio}
                />
              ))}

              {/* Create New Card */}
              <Card
                className="p-6 border-2 border-dashed border-gray-300 hover:border-emerald-500 hover:bg-gray-50 cursor-pointer transition-all duration-200 flex flex-col items-center justify-center min-h-[400px]"
                onClick={handleCreateNew}
              >
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Plus className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Create New Portfolio</h3>
                <p className="text-sm text-gray-500 text-center">Start tracking your investments</p>
              </Card>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-8 border-t border-gray-200 text-sm text-gray-500">
          <div className="flex gap-6">
            <span>© 2024 TradingJournal. All rights reserved.</span>
            <a href="#" className="hover:text-gray-700">Privacy Policy</a>
            <a href="#" className="hover:text-gray-700">Terms of Service</a>
            <a href="#" className="hover:text-gray-700">Support</a>
          </div>
          <div className="flex items-center gap-2">
            <span>Last updated: 2 minutes ago</span>
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {editingPortfolio && (
        <EditPortfolioModal
          isOpen={!!editingPortfolio}
          onClose={() => setEditingPortfolio(null)}
          portfolio={editingPortfolio}
          onSave={handleUpdatePortfolio}
        />
      )}
      <CreatePortfolioModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreatePortfolio}
      />
    </div>
  );
};

export default HomePage;
